import { formatDate } from '../utils/dateConversion'
import { PLANS } from '../utils/planNames'
import { AdapterService } from './AdapterService'
import axiosInstance from './axiosInstance'

export const STATUSES = {
  SUCCESS: 'success',
  FAILURE: 'failure',
}

const login = async ({ loginKey, password }) => {
  try {
    const response = await axiosInstance.post(`/login`, {
      user: {
        email: loginKey,
        password: password,
      },
    })

    const { data } = response.data

    return {
      status: STATUSES.SUCCESS,
      data,
    }
  } catch (e) {
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getUserOnboardingPreferences = async () => {
  try {
    const { data } = await axiosInstance.get(`/onboarding`)

    return AdapterService.handleUserOnboardingPreferences({
      status: STATUSES.SUCCESS,
      data,
    })
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const saveUserOnboardingPreferences = async ({
  shift,
  sleepTime,
  awakeTime,
  caffeinePreferences,
  caffeineTime,
}) => {
  try {
    await axiosInstance.post(`/onboarding/user-preferences`, {
      ialerta_default_config: {
        user_shift: {
          confirmed: shift,
        },
        sleep_start: sleepTime,
        sleep_end: awakeTime,
        caffeine_product_ids: caffeinePreferences,
        caffeine_start: caffeineTime,
      },
    })

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const getUserPreferences = async () => {
  try {
    const { data } = await axiosInstance.get(`/intake`)

    return AdapterService.handleUserPreferences({
      status: STATUSES.SUCCESS,
      data,
    })
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const saveUserIntake = async ({
  sleepTime,
  awakeTime,
  caffeinePreferences,
  caffeineTime,
}) => {
  const result = AdapterService.handleSaveUserIntake({
    sleepTime,
    awakeTime,
    caffeinePreferences,
    caffeineTime,
  })

  try {
    await axiosInstance.post(`/intake`, {
      intake: {
        ...result,
      },
    })

    return { status: STATUSES.SUCCESS, data: {} }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const updateIntake = async ({
  id,
  type,
  sleepTime,
  awakeTime,
  caffeinePreferences,
  caffeineTime,
}) => {
  const result = AdapterService.handleUpdateIntake({
    id,
    type,
    sleepTime,
    awakeTime,
    caffeinePreferences,
    caffeineTime,
  })

  try {
    await axiosInstance.patch(`/intake`, {
      intake: {
        ...result,
      },
    })

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const getDashboard = async () => {
  try {
    const response = await axiosInstance.get(`/dashboard`)
    const { user_dashboard, data } = response?.data

    const userDashboard = user_dashboard

    const recommendations = data?.caffeine_events?.map(
      ({ caffeine_product_name, timestamp, product_image_tag, id, status }) => {
        return {
          id,
          status,
          caffeineProduct: caffeine_product_name,
          caffeineHour: timestamp,
          imgTag: product_image_tag,
        }
      },
    )

    const ingestedCaffeine = data?.caffeine_intakes?.map(
      ({ timestamp, caffeine_product_names }) => {
        return {
          caffeineHour: timestamp,
          caffeineProducts: caffeine_product_names,
        }
      },
    )

    const predictionData = data?.prediction_data?.map(status => {
      return {
        timestamp: status.timestamp,
        prediction: status.prediction,
        predictionResult: status.prediction_result,
      }
    })

    const userShifts = data?.shift_events
    const sleepTime = data?.sleep_events
    const bacLevels = data?.bac_levels

    if (userDashboard === PLANS.PREDICTION)
      return {
        status: STATUSES.SUCCESS,
        data: {
          userDashboard,
          recommendations,
          ingestedCaffeine,
          userShifts,
          sleepTime,
          predictionData,
          optimizeData: null,
          bacLevels,
        },
      }

    const optimizeData = data?.prediction_data?.map(status => {
      return {
        timestamp: status.timestamp,
        optimization: status.optimization,
        optimizationResult: status.optimization_result,
      }
    })

    return {
      status: STATUSES.SUCCESS,
      data: {
        userDashboard,
        recommendations,
        ingestedCaffeine,
        userShifts,
        sleepTime,
        predictionData,
        optimizeData,
        bacLevels,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getDashboardByEmployeeId = async ({ employeeId }) => {
  try {
    const response = await axiosInstance.get(
      `/supervised-dashboard?user_id=${employeeId}`,
    )
    const { user_dashboard, data } = response?.data

    const userDashboard = user_dashboard

    const recommendations = data?.caffeine_events?.map(
      ({
        caffeine_product_name,
        caffeine_start,
        product_image_tag,
        status,
      }) => {
        return {
          status,
          caffeineProduct: caffeine_product_name,
          caffeineHour: new Date(caffeine_start).getTime(),
          imgTag: product_image_tag,
        }
      },
    )

    const ingestedCaffeine = data?.caffeine_intakes?.map(({ timestamp }) => {
      return {
        caffeineHour: timestamp,
      }
    })

    const predictionData = data?.prediction_data?.map(status => {
      return {
        timestamp: status.timestamp,
        prediction: status.prediction,
        predictionResult: status.prediction_result,
      }
    })

    const userShifts = data?.shift_events
    const sleepTime = data?.sleep_events

    const bacLevels = data?.bac_levels

    if (userDashboard === PLANS.PREDICTION)
      return {
        status: STATUSES.SUCCESS,
        data: {
          userDashboard,
          recommendations,
          userShifts,
          sleepTime,
          predictionData,
          optimizeData: null,
          ingestedCaffeine,
          bacLevels,
        },
      }

    const optimizeData = data?.prediction_data?.map(status => {
      return {
        timestamp: status.timestamp,
        optimization: status.optimization,
        optimizationResult: status.optimization_result,
      }
    })

    return {
      status: STATUSES.SUCCESS,
      data: {
        userDashboard,
        recommendations,
        userShifts,
        sleepTime,
        predictionData,
        optimizeData,
        ingestedCaffeine,
        bacLevels,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getHistoricalMetrics = async () => {
  try {
    const { data } = await axiosInstance.get(`/history-metrics`)

    const normalizedData = AdapterService.handleHistoricalMetrics({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const getDashboardNotifications = async () => {
  try {
    const { data } = await axiosInstance.get(`/supervisor-notifications`)

    const normalizedData = AdapterService.handleSupervisorNotifications({
      data,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const getDashboardKPIs = async () => {
  try {
    const { data } = await axiosInstance.get(`/supervisor-kpis`)

    const normalizedData = AdapterService.handleDashboardKPIs({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const getPreSelectedUsers = async ({ url }) => {
  try {
    const { data } = await axiosInstance.get(`/${url}`)

    return {
      status: STATUSES.SUCCESS,
      data,
    }
  } catch (e) {
    console.log(e)
  }
}

const createNotification = async ({ users, message, severity }) => {
  try {
    await axiosInstance.post(`/notifications`, {
      notifications: {
        user_ids: users,
        message,
        severity_level: severity,
      },
    })

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    console.log(e)
  }
}

const createAnnotation = async ({ users, message, severity, status }) => {
  try {
    await axiosInstance.post(`/interventions`, {
      interventions: {
        user_ids: users,
        message,
        severity_level: severity,
        status,
      },
    })

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    console.log(e)
  }
}

const getUsers = async ({ visibility } = {}) => {
  try {
    const { data } = await axiosInstance.get(`user-list`, { params: { visibility } })

    return {
      status: STATUSES.SUCCESS,
      data,
    }
  } catch (e) {
    console.log(e)
  }
}

const getColumns = async () => {
  try {
    const { data } = await axiosInstance.get(`/dashboard-column-configs`)

    const normalizedData = AdapterService.handleGetColumns({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const saveColumns = async ({
  id,
  highFatigue,
  notification,
  counterMeasure,
  fatigueLevel8h,
  intakePending,
}) => {
  try {
    await axiosInstance.post(`/dashboard-column-configs`, {
      dashboard_column_config: {
        id,
        employees: true,
        high_fatigue: highFatigue,
        notification,
        counter_measure: counterMeasure,
        fatigue_level_8h: fatigueLevel8h,
        intake_pending: intakePending,
      },
    })

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    console.log(e)
  }
}

const getVisualManagementUsersData = async ({ orderBy }) => {
  try {
    const { data } = await axiosInstance.get(
      `/supervisor-management?sort_by=${orderBy}`,
    )

    const normalizedData = AdapterService.handleVisualManagementUsersData({
      data: data.data,
      graphMessage: data.data_message,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const getEmployeeKPIs = async ({ employeeId }) => {
  try {
    const { data } = await axiosInstance.get(
      `/user-kpis?user_ids=${employeeId}`,
    )

    const normalizedData = AdapterService.handleDashboardKPIs({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const getUserPlan = async () => {
  try {
    const { data } = await axiosInstance.get('/user-plan')

    const normalizedData = AdapterService.handleUserPlan({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const getSentNotifications = async ({ startDate, endDate }) => {
  try {
    const url =
      startDate && endDate
        ? `/sent-notifications?start_date=${formatDate({ date: startDate })}&end_date=${formatDate({ date: endDate })}`
        : `/sent-notifications`

    const { data } = await axiosInstance.get(url)

    const normalizedData = AdapterService.handleNotifications({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const getReceivedNotifications = async ({ startDate, endDate }) => {
  try {
    const url =
      startDate && endDate
        ? `/received-notifications?start_date=${formatDate({ date: startDate })}&end_date=${formatDate({ date: endDate })}`
        : `/received-notifications`

    const { data } = await axiosInstance.get(url)

    const normalizedData = AdapterService.handleNotifications({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const getSentAnnotations = async ({ startDate, endDate }) => {
  try {
    const url =
      startDate && endDate
        ? `/sent-interventions?start_date=${formatDate({ date: startDate })}&end_date=${formatDate({ date: endDate })}`
        : `/sent-interventions`

    const { data } = await axiosInstance.get(url)

    console.log(data)
    const normalizedData = AdapterService.handleInterventions({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const getReceivedAnnotations = async ({ startDate, endDate }) => {
  try {
    const url =
      startDate && endDate
        ? `/received-interventions?start_date=${formatDate({ date: startDate })}&end_date=${formatDate({ date: endDate })}`
        : `/received-interventions`

    const { data } = await axiosInstance.get(url)
    console.log(data)
    const normalizedData = AdapterService.handleInterventions({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const updateAnnotation = async ({ id, update_type, value }) => {
  try {
    await axiosInstance.patch(`/intervention`, {
      intervention: {
        id,
        update_type,
        value,
      },
    })

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const getSystemEvents = async ({ startDate, endDate }) => {
  try {
    const { data } = await axiosInstance.get(
      `/system-events?start_date=${formatDate({ date: startDate })}&end_date=${formatDate({ date: endDate })}`,
    )

    const normalizedData = AdapterService.handleSystemEvents({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const getReportOptimizeCounterMeasure = async ({
  groupBy,
  orderBy,
  selectedFilterOptions,
  selectedUserId,
}) => {
  const { shifts, teams, titles, levels } = AdapterService.handleDefaultParams({
    selectedFilterOptions,
  })

  try {
    let url = `/supervisor-management?sort_by=${orderBy}&group_by=${groupBy}`

    if (shifts) url = `${url}&shifts=${shifts}`
    if (teams) url = `${url}&teams=${teams}`
    if (titles) url = `${url}&titles=${titles}`
    if (levels) url = `${url}&levels=${levels}`
    if (selectedUserId) url = `${url}&user_id=${selectedUserId}`

    const { data } = await axiosInstance.get(url)

    const normalizedData = AdapterService.handleReportOptimizeCounterMeasure({
      data: data.data,
      graphMessage: data.data_message,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
  }
}

const getContextInfo = async () => {
  try {
    const { data } = await axiosInstance.get(`/user-config`)

    return {
      status: STATUSES.SUCCESS,
      data,
    }
  } catch (e) {
    console.log(e)
  }
}

const createPVT = async ({ pvtId, reactions }) => {
  try {
    await axiosInstance.post('/pvt', {
      pvt: { pvt_id: pvtId, events: [...reactions] },
    })

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getPvtUserInfo = async () => {
  try {
    const { data } = await axiosInstance.get('/user-pvt')

    return {
      status: STATUSES.SUCCESS,
      data: data,
    }
  } catch (e) {
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const pvtStarted = async ({
  device_model,
  device_os,
  is_mobile,
  is_desktop,
  is_tablet,
}) => {
  try {
    const { data } = await axiosInstance.post('/pvt-start', {
      device_model,
      device_os,
      is_mobile,
      is_desktop,
      is_tablet,
    })

    const normalizedData = AdapterService.handlePvtStarted({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getPvtStatus = async () => {
  try {
    const { data } = await axiosInstance.get('/pvt-status')

    const normalizedData = AdapterService.handlePvtStatus({
      data,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getUserPendingActions = async () => {
  try {
    const { data } = await axiosInstance.get('/user-pending-actions')

    const normalizedData = AdapterService.handleUserPendingActions({
      data,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const readNotification = async ({ id, acknowledged }) => {
  try {
    await axiosInstance.patch(`/acknowledge-notification`, {
      notifications: {
        id,
        acknowledged,
      },
    })

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    console.log(e)
  }
}

const getUnreadNotificationCount = async () => {
  try {
    const { data } = await axiosInstance.get(`/notifications-count`)

    const normalizedData = AdapterService.handleUnreadNotificationCount({
      data,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getImports = async ({ type }) => {
  try {
    const { data } = await axiosInstance.get(`/${type}-imports`)

    const normalizedData = AdapterService.handleGetImport({
      data,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const postImports = async ({ type, data }) => {
  try {
    await axiosInstance.post(
      `/${type}-imports`,
      { file: data },
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    console.log(e)

    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const postDevicePermission = async ({ data }) => {
  try {
    await axiosInstance.post(`/device-permission`, { data })

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getAccountConfigurations = async () => {
  try {
    const { data } = await axiosInstance.get(`/account-config`)
    const normalizedData = AdapterService.handleAccountConfig({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getIntakeList = async ({ userId }) => {
  try {
    const url = userId ? `/list-intake?user_id=${userId}` : '/list-intake'
    const { data } = await axiosInstance.get(url)

    const normalizedData = AdapterService.handleIntakeList({
      data,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const updateAccountConfigurations = async ({ formData }) => {
  try {
    await axiosInstance.patch(`/account-config`, {
      account_configuration: formData,
    })

    return { status: STATUSES.SUCCESS }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const updateAccountCaffeineConfig = async ({ caffeineRows }) => {
  try {
    const { data } = await axiosInstance.patch(`/account-caffeine-config`, {
      account_caffeine_configuration: caffeineRows,
    })

    return {
      status: STATUSES.SUCCESS,
      data,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const getPredictionHistoryFilters = async () => {
  try {
    const { data } = await axiosInstance.get('/prediction-filters')

    const normalizedData = AdapterService.handleDefaultFilters({
      data,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getPredictionHistory = async ({
  date,
  selectedFilterOptions,
  selectedUserId,
  onShift,
}) => {
  const { start_date, end_date, shifts, teams, titles, levels } =
    AdapterService.handleDefaultParams({
      date,
      selectedFilterOptions,
    })

  try {
    let url = `/prediction-history?start_date=${start_date}&end_date=${end_date}`

    if (shifts) url = `${url}&shifts=${shifts}`
    if (teams) url = `${url}&teams=${teams}`
    if (titles) url = `${url}&titles=${titles}`
    if (levels) url = `${url}&levels=${levels}`
    if (selectedUserId) url = `${url}&user_id=${selectedUserId}`
    if (onShift) url = `${url}&on_shift=${onShift}`

    const { data } = await axiosInstance.get(url)

    const normalizedData = AdapterService.handlePredictionHistory({
      data: data.data,
      graphMessage: data.data_message,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getShiftFatigue = async ({
  date,
  selectedFilterOptions,
  selectedUserId,
  onShift,
}) => {
  const { start_date, end_date, shifts, teams, titles, levels } =
    AdapterService.handleDefaultParams({
      date,
      selectedFilterOptions,
    })

  try {
    let url = `/shift-fatigue?start_date=${start_date}&end_date=${end_date}`

    if (shifts) url = `${url}&shifts=${shifts}`
    if (teams) url = `${url}&teams=${teams}`
    if (titles) url = `${url}&titles=${titles}`
    if (levels) url = `${url}&levels=${levels}`
    if (selectedUserId) url = `${url}&user_id=${selectedUserId}`
    if (onShift) url = `${url}&on_shift=${onShift}`

    const { data } = await axiosInstance.get(url)

    const normalizedData = AdapterService.handleShiftFatigue({
      data: data.data,
      graphMessage: data.data_message,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getShiftFatigueFilters = async () => {
  try {
    const { data } = await axiosInstance.get('/prediction-filters')

    const normalizedData = AdapterService.handleDefaultFilters({
      data,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getFatigueEvolution = async ({
  date,
  selectedFilterOptions,
  selectedUserId,
  onShift,
  granularity,
}) => {
  const { start_date, end_date, shifts, teams, titles, levels } =
    AdapterService.handleDefaultParams({
      date,
      selectedFilterOptions,
    })

  try {
    let url = `/fatigue-evolution?start_date=${start_date}&end_date=${end_date}`

    if (shifts) url = `${url}&shifts=${shifts}`
    if (teams) url = `${url}&teams=${teams}`
    if (titles) url = `${url}&titles=${titles}`
    if (levels) url = `${url}&levels=${levels}`
    if (selectedUserId) url = `${url}&user_id=${selectedUserId}`
    if (onShift) url = `${url}&on_shift=${onShift}`
    if (granularity) url = `${url}&granularity=${granularity}`

    const { data } = await axiosInstance.get(url)

    const normalizedData = AdapterService.handleFatigueEvolution({
      data: data.data,
      graphMessage: data.data_message,
      granularity,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getFatigueEvolutionFilters = async () => {
  try {
    const { data } = await axiosInstance.get('/fatigue-evolution-filters')

    const normalizedData = AdapterService.handleDefaultFilters({
      data,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getSleepQuantity = async ({
  date,
  selectedFilterOptions,
  selectedUserId,
  granularity,
}) => {
  const { start_date, end_date, shifts, teams, titles, levels } =
    AdapterService.handleDefaultParams({
      date,
      selectedFilterOptions,
    })

  try {
    let url = `/sleep-quantity?start_date=${start_date}&end_date=${end_date}`

    if (shifts) url = `${url}&shifts=${shifts}`
    if (teams) url = `${url}&teams=${teams}`
    if (titles) url = `${url}&titles=${titles}`
    if (levels) url = `${url}&levels=${levels}`
    if (selectedUserId) url = `${url}&user_id=${selectedUserId}`
    if (granularity) url = `${url}&granularity=${granularity}`

    const { data } = await axiosInstance.get(url)

    const normalizedData = AdapterService.handleSleepQuantity({
      data: data.data,
      graphMessage: data.data_message,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getSleepQuantityFilters = async () => {
  try {
    const { data } = await axiosInstance.get('/sleep-filters')

    const normalizedData = AdapterService.handleDefaultFilters({
      data,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getDistributionFatigue = async ({
  date,
  selectedFilterOptions,
  selectedUserId,
}) => {
  const { start_date, end_date, shifts, teams, titles, levels } =
    AdapterService.handleDefaultParams({
      date,
      selectedFilterOptions,
    })

  try {
    let url = `/fatigue-distribution?start_date=${start_date}&end_date=${end_date}`

    if (shifts) url = `${url}&shifts=${shifts}`
    if (teams) url = `${url}&teams=${teams}`
    if (titles) url = `${url}&titles=${titles}`
    if (levels) url = `${url}&levels=${levels}`
    if (selectedUserId) url = `${url}&user_id=${selectedUserId}`

    const { data } = await axiosInstance.get(url)

    const normalizedData = AdapterService.handleDistributionFatigue({
      data: data.data,
      graphMessage: data.data_message,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getDistributionFatigueFilters = async () => {
  try {
    const { data } = await axiosInstance.get('/fatigue-distribution-filters')

    const normalizedData = AdapterService.handleDefaultFilters({
      data,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const updateRecommendation = async ({ id, recommendationStatus }) => {
  try {
    await axiosInstance.patch(`/recommendation`, {
      recommendation: {
        id,
        recommendation_status: recommendationStatus,
      },
    })

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const getAccountSubscriptionData = async accountId => {
  try {
    const { data } = await axiosInstance.get(
      `/account-subscription?account_id=${accountId}`,
    )

    return {
      status: STATUSES.SUCCESS,
      data,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getAdminCockpitData = async () => {
  try {
    const { data } = await axiosInstance.get(`/admin-config`)
    const normalizedData = AdapterService.handleAdminCockpitData({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const updateSystemConfiguration = async ({ formData }) => {
  try {
    await axiosInstance.patch(`/system-config`, {
      account_configuration: formData,
    })

    return { status: STATUSES.SUCCESS }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const updateAllAccounts = async ({ formData }) => {
  try {
    await axiosInstance.patch(`/all-accounts`, {
      account_configuration: formData,
    })

    return { status: STATUSES.SUCCESS }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const updateCurrentAccount = async ({ current_account_id }) => {
  try {
    await axiosInstance.patch(`/current-account`, {
      current_account_id,
    })

    return { status: STATUSES.SUCCESS }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const updateUserAccount = async ({ current_account_id }) => {
  try {
    await axiosInstance.patch(`/user-account-change`, {
      current_account_id,
    })

    return { status: STATUSES.SUCCESS }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const getAccountConfiguration = async id => {
  try {
    const { data } = await axiosInstance.get(`/account-config?id=${id}`)
    const normalizedData = AdapterService.handleAccountConfig({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getUserFullList = async ({ selectedFilterOptions, selectedUserId }) => {
  const { shifts, teams, titles, levels } = AdapterService.handleDefaultParams({
    selectedFilterOptions,
  })

  try {
    let url = `/user-full-list?`

    if (shifts) url = `${url}&shifts=${shifts}`
    if (teams) url = `${url}&teams=${teams}`
    if (titles) url = `${url}&titles=${titles}`
    if (levels) url = `${url}&levels=${levels}`
    if (selectedUserId) url = `${url}&user_id=${selectedUserId}`
    const { data } = await axiosInstance.get(url)
    const normalizedData = AdapterService.handleUserFullList({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getUserFullListFilters = async () => {
  try {
    const { data } = await axiosInstance.get('/user-full-list-filters')
    const normalizedData = AdapterService.handleDefaultFilters({
      data,
    })
    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getInputDataQuality = async ({
  selectedFilterOptions,
  selectedUserId,
}) => {
  const { shifts, teams, titles, levels } = AdapterService.handleDefaultParams({
    selectedFilterOptions,
  })
  try {
    let url = `/input-data-quality?`

    if (shifts) url = `${url}&shifts=${shifts}`
    if (teams) url = `${url}&teams=${teams}`
    if (titles) url = `${url}&titles=${titles}`
    if (levels) url = `${url}&levels=${levels}`
    if (selectedUserId) url = `${url}&user_id=${selectedUserId}`
    const { data } = await axiosInstance.get(url)
    const normalizedData = AdapterService.handleInputDataQuality({
      data,
    })
    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getInputDataQualityFilters = async () => {
  try {
    const { data } = await axiosInstance.get('/input-data-quality-filters')
    const normalizedData = AdapterService.handleDefaultFilters({
      data,
    })
    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getUserListActionsData = async () => {
  try {
    const { data } = await axiosInstance.get('/user-list-actions')
    return {
      status: STATUSES.SUCCESS,
      data: data,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const updateUserList = async ({
  users,
  status,
  shift,
  supervisors,
  supervisorAction,
}) => {
  try {
    await axiosInstance.post(`/update-user-list`, {
      users,
      status,
      shift,
      supervisors,
      supervisor_action: supervisorAction,
    })

    return { status: STATUSES.SUCCESS }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const createUser = async ({ userData }) => {
  try {
    await axiosInstance.post(`/create-user`, {
      user_data: userData,
    })

    return { status: STATUSES.SUCCESS }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const exportData = async ({ formData }) => {
  try {
    const response = await axiosInstance.post(
      `/data-export`,
      { export_options: formData },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/zip',
        },
        responseType: 'blob',
      },
    )
    return {
      status: STATUSES.SUCCESS,
      data: response.data,
    }
  } catch (e) {
    console.log(e)

    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const getUserData = async id => {
  try {
    const { data } = await axiosInstance.get(`/user-data?user_id=${id}`)
    return {
      status: STATUSES.SUCCESS,
      data: data,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const updateUser = async ({ userData }) => {
  try {
    await axiosInstance.post(`/update-user?user_id=${userData.id}`, {
      user_data: userData,
    })

    return { status: STATUSES.SUCCESS }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const getShiftList = async () => {
  try {
    let url = '/shift-list'

    const { data } = await axiosInstance.get(url)

    return {
      status: STATUSES.SUCCESS,
      data: data,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

const deleteIntake = async ({ id, type }) => {
  try {
    await axiosInstance.post(`/delete-intake`, {
      intake: {
        id: id,
        type: type,
      },
    })

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const deleteSchedule = async id => {
  try {
    await axiosInstance.delete(`/schedule/${id}`)

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const createOrUpdateAccountAndSubscription = async ({
  account,
  subscription,
}) => {
  try {
    await axiosInstance.post(`/account-subscription`, {
      ...account,
      ...subscription,
    })

    return {
      status: STATUSES.SUCCESS,
      data: {},
    }
  } catch (e) {
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const fetchAccountAndSubscription = async () => {
  try {
    const { data } = await axiosInstance.get(`/account-subscription`)
    const normalizedData = AdapterService.handleAccountSubscriptionData({
      data,
    })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    return {
      status: STATUSES.FAILURE,
      data: {
        message: e.response?.data?.message,
      },
    }
  }
}

const getAccountChangeData = async () => {
  try {
    const { data } = await axiosInstance.get(`/account-change`)
    const normalizedData = AdapterService.handleAccountChangeData({ data })

    return {
      status: STATUSES.SUCCESS,
      data: normalizedData,
    }
  } catch (e) {
    console.log(e)
    return {
      status: STATUSES.FAILURE,
      data: {},
    }
  }
}

export const RequestService = {
  STATUSES,
  login,
  getUserOnboardingPreferences,
  saveUserOnboardingPreferences,
  getUserPreferences,
  saveUserIntake,
  getDashboard,
  getHistoricalMetrics,
  getDashboardNotifications,
  getDashboardKPIs,
  getPreSelectedUsers,
  createNotification,
  createAnnotation,
  getUsers,
  getColumns,
  saveColumns,
  getVisualManagementUsersData,
  getDashboardByEmployeeId,
  getEmployeeKPIs,
  getUserPlan,
  getSentNotifications,
  getReceivedNotifications,
  getSystemEvents,
  getReportOptimizeCounterMeasure,
  getContextInfo,
  createPVT,
  getPvtUserInfo,
  pvtStarted,
  getPvtStatus,
  getUserPendingActions,
  readNotification,
  getUnreadNotificationCount,
  getImports,
  postImports,
  postDevicePermission,
  getAccountConfigurations,
  updateAccountConfigurations,
  getIntakeList,
  updateIntake,
  getPredictionHistory,
  getPredictionHistoryFilters,
  getShiftFatigue,
  getShiftFatigueFilters,
  getFatigueEvolution,
  getFatigueEvolutionFilters,
  getSleepQuantity,
  getSleepQuantityFilters,
  getDistributionFatigueFilters,
  getDistributionFatigue,
  updateRecommendation,
  getAdminCockpitData,
  updateSystemConfiguration,
  updateAllAccounts,
  updateCurrentAccount,
  getAccountConfiguration,
  getUserFullList,
  getUserFullListFilters,
  getInputDataQuality,
  getInputDataQualityFilters,
  getUserListActionsData,
  updateUserList,
  createUser,
  exportData,
  getUserData,
  updateUser,
  getShiftList,
  deleteIntake,
  deleteSchedule,
  updateAccountCaffeineConfig,
  getReceivedAnnotations,
  getSentAnnotations,
  updateAnnotation,
  createOrUpdateAccountAndSubscription,
  fetchAccountAndSubscription,
  getAccountSubscriptionData,
  getAccountChangeData,
  updateUserAccount,
}
